import { PAINT_ALIASES, ALIAS_INDEX, normaliseQuery } from "@/data/paintAliases";
import api from "@/lib/api";

/**
 * Paint colour search — thin wrapper over the scalable alias API.
 *
 * ─── Architecture (adopted 2026-07-07 · PAINT-SEARCH-02) ─────────────
 * The alias table lives in Mongo (`paint_aliases`) and is queried via
 * `GET /api/paint-aliases/search?q=…`. The response includes the RESOLVED
 * ColourMatch library code + hex + name — the frontend never renders the
 * trade colour's ref_hex directly. This preserves the single-source-of-
 * truth rule: only real ColourMatch library colours ever reach the
 * viewport, the render pipeline, or a saved scheme.
 *
 * The legacy in-memory alias file (`/app/frontend/src/data/paintAliases.js`)
 * is retained ONLY as an offline fallback for when the API is unreachable
 * (network hiccup, slow first-paint). Once the DB path has soaked for one
 * release cycle we can drop it entirely.
 * ─────────────────────────────────────────────────────────────────────
 *
 * Two-stage matching:
 *   1. Direct name search over the ColourMatch library (substring + word-
 *      set). This runs client-side against the palette already in memory.
 *   2. Branded-alias resolution via the API. If the query looks like a
 *      Dulux/Resene/Taubmans/Wattyl/… colour, the alias endpoint returns
 *      the pre-resolved `library_code` — we surface that swatch as the
 *      "Closest ColourMatch match" and never claim it IS the branded
 *      colour.
 */

// ─── Hex → sRGB → linear RGB → XYZ → CIE Lab ─────────────────────────
function hexToRgb(hex) {
  const h = hex.replace("#", "");
  return [
    parseInt(h.substring(0, 2), 16) / 255,
    parseInt(h.substring(2, 4), 16) / 255,
    parseInt(h.substring(4, 6), 16) / 255,
  ];
}
function srgbToLinear(c) {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}
function rgbToXyz([r, g, b]) {
  const R = srgbToLinear(r), G = srgbToLinear(g), B = srgbToLinear(b);
  return [
    (R * 0.4124564 + G * 0.3575761 + B * 0.1804375) * 100,
    (R * 0.2126729 + G * 0.7151522 + B * 0.0721750) * 100,
    (R * 0.0193339 + G * 0.1191920 + B * 0.9503041) * 100,
  ];
}
const REF_X = 95.047, REF_Y = 100.000, REF_Z = 108.883;
function xyzToLab([x, y, z]) {
  const fx = pivot(x / REF_X), fy = pivot(y / REF_Y), fz = pivot(z / REF_Z);
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}
function pivot(t) {
  return t > 0.008856 ? Math.cbrt(t) : (7.787 * t) + 16 / 116;
}
export function hexToLab(hex) {
  return xyzToLab(rgbToXyz(hexToRgb(hex)));
}
export function deltaE76(labA, labB) {
  const dL = labA[0] - labB[0], da = labA[1] - labB[1], db = labA[2] - labB[2];
  return Math.sqrt(dL * dL + da * da + db * db);
}

// ─── Name-based scoring over the ColourMatch library ─────────────────
function tokens(s) {
  return normaliseQuery(s).split(" ").filter(Boolean);
}
function wordOverlapScore(qTokens, targetTokens) {
  if (qTokens.length === 0) return 0;
  const set = new Set(targetTokens);
  const hits = qTokens.filter((t) => set.has(t)).length;
  return hits / qTokens.length;
}
function nameScore(query, name) {
  const q = normaliseQuery(query);
  const n = normaliseQuery(name);
  if (!q) return 0;
  if (n === q) return 1.0;
  if (n.startsWith(q)) return 0.95;
  if (n.includes(q)) return 0.85;
  if (q.includes(n) && n.length >= 4) return 0.80;
  return wordOverlapScore(tokens(q), tokens(n)) * 0.70;
}

export function nearestColour(hex, palette) {
  const target = hexToLab(hex);
  let best = { distance: Infinity, colour: null };
  for (const c of palette) {
    const d = deltaE76(target, hexToLab(c.hex));
    if (d < best.distance) best = { distance: d, colour: c };
  }
  return best;
}

// ─── Offline fallback: use the embedded PAINT_ALIASES file ───────────
function offlineAliasHit(query) {
  const q = normaliseQuery(query);
  if (q.length < 3) return null;
  let best = { score: 0, idx: -1 };
  for (const { i, norm } of ALIAS_INDEX) {
    let s = 0;
    if (norm === q) s = 1.0;
    else if (norm.includes(q)) s = 0.95;
    else if (q.includes(norm)) s = 0.90;
    else s = wordOverlapScore(tokens(q), tokens(norm)) * 0.85;
    if (s > best.score) best = { score: s, idx: i };
  }
  return best.score >= 0.6 ? { alias: PAINT_ALIASES[best.idx], confidence: best.score } : null;
}

// ─── API-backed alias lookup (with request-level memoisation) ────────
// Keyed by normalised query; expires when the tab is refreshed.
const _apiCache = new Map();
export function _resetPaintSearchCache() { _apiCache.clear(); }

async function fetchAliasFromApi(query) {
  const q = normaliseQuery(query);
  if (q.length < 2) return null;
  if (_apiCache.has(q)) return _apiCache.get(q);

  const promise = (async () => {
    try {
      const { data } = await api.get("/paint-aliases/search", {
        params: { q, limit: 1 },
        // 4-second cap so a slow API never blocks the UI; the direct
        // library search still runs in parallel.
        timeout: 4000,
      });
      return data?.results?.[0] || null;
    } catch {
      return null;  // caller will fall back to the offline file
    }
  })();

  _apiCache.set(q, promise);
  return promise;
}

/**
 * Sync search — resolves ColourMatch library direct-name matches only.
 * Alias matching is now async and lives in `searchPaletteAsync`; this
 * synchronous variant is kept for legacy call sites that render on every
 * keystroke without awaiting a network round-trip.
 */
export function searchPalette(query, palette, nameScoreThreshold = 0.7) {
  const q = (query || "").trim();
  if (!q) return { pinned: null, directMatches: [] };

  // Fast, offline alias check on the embedded file — if we hit an alias
  // with ≥0.8 confidence AND the user has typed ≥2 tokens, resolve
  // locally via nearestColour(). This preserves the pre-refactor UX for
  // the initial keystroke burst, while the async API-backed path
  // (searchPaletteAsync) refines the pin as soon as it responds.
  const qTokensCount = tokens(q).length;
  const aliasHit = offlineAliasHit(q);
  if (aliasHit && aliasHit.confidence >= 0.8 && qTokensCount >= 2) {
    const near = nearestColour(aliasHit.alias.hex, palette);
    if (near.colour) {
      return {
        pinned: {
          colour: near.colour,
          matchType: "alias",
          aliasSource: aliasHit.alias,
          distance: near.distance,
        },
        directMatches: [],
      };
    }
  }

  // Direct name search over the ColourMatch library.
  const scored = palette
    .map((c) => ({ ...c, score: nameScore(q, c.name) }))
    .filter((c) => c.score >= nameScoreThreshold)
    .sort((a, b) => b.score - a.score);
  if (scored.length > 0) {
    return {
      pinned: { colour: scored[0], matchType: "direct" },
      directMatches: scored.slice(0, 12),
    };
  }

  // Lower-confidence offline alias fallback.
  if (aliasHit && aliasHit.confidence >= 0.6) {
    const near = nearestColour(aliasHit.alias.hex, palette);
    if (near.colour) {
      return {
        pinned: {
          colour: near.colour,
          matchType: "alias",
          aliasSource: aliasHit.alias,
          distance: near.distance,
        },
        directMatches: [],
      };
    }
  }

  // Loose name-match tail.
  const loose = palette
    .map((c) => ({ ...c, score: nameScore(q, c.name) }))
    .filter((c) => c.score >= 0.35)
    .sort((a, b) => b.score - a.score)
    .slice(0, 6);
  return { pinned: null, directMatches: loose };
}

/**
 * Async search — the preferred entry point for components that can
 * await. Calls the DB-backed alias API for a richer result set. The
 * returned `pinned.colour` is ALWAYS a real row from the passed `palette`
 * (looked up by `library_code` returned by the API), preserving the SSOT
 * rule end-to-end.
 */
export async function searchPaletteAsync(query, palette, nameScoreThreshold = 0.7) {
  const q = (query || "").trim();
  if (!q) return { pinned: null, directMatches: [] };
  const qTokensCount = tokens(q).length;

  // Kick off API lookup and direct-name search in parallel.
  const apiPromise = fetchAliasFromApi(q);

  // Palette rows are keyed by `hex` in the homeowner API; resolve API
  // alias hits by matching the returned `library_hex` against the loaded
  // palette. If we can't find the swatch locally we drop the pin —
  // never fall back to `ref_hex` (SSOT rule).
  const resolveByHex = (targetHex) => {
    if (!targetHex) return null;
    const norm = String(targetHex).toUpperCase();
    return palette.find((c) => String(c.hex || "").toUpperCase() === norm) || null;
  };

  // Stage 1 — API alias match. If confidence ≥0.8 AND ≥2 tokens, pin.
  const apiHit = await apiPromise;
  if (apiHit && apiHit.confidence >= 0.8 && qTokensCount >= 2) {
    const palRow = resolveByHex(apiHit.library_hex);
    if (palRow) {
      return {
        pinned: {
          colour: palRow,
          matchType: "alias",
          aliasSource: {
            brand: apiHit.brand,
            displayName: apiHit.trade_name,
            hex: apiHit.ref_hex,
          },
          distance: apiHit.delta_e,
        },
        directMatches: [],
      };
    }
  }

  // Stage 2 — direct name search over the library.
  const scored = palette
    .map((c) => ({ ...c, score: nameScore(q, c.name) }))
    .filter((c) => c.score >= nameScoreThreshold)
    .sort((a, b) => b.score - a.score);
  if (scored.length > 0) {
    return {
      pinned: { colour: scored[0], matchType: "direct" },
      directMatches: scored.slice(0, 12),
    };
  }

  // Stage 3 — lower-confidence API alias fallback.
  if (apiHit && apiHit.confidence >= 0.6) {
    const palRow = resolveByHex(apiHit.library_hex);
    if (palRow) {
      return {
        pinned: {
          colour: palRow,
          matchType: "alias",
          aliasSource: {
            brand: apiHit.brand,
            displayName: apiHit.trade_name,
            hex: apiHit.ref_hex,
          },
          distance: apiHit.delta_e,
        },
        directMatches: [],
      };
    }
  }

  // Stage 4 — offline alias fallback (network down / API errored).
  const offline = offlineAliasHit(q);
  if (offline && offline.confidence >= 0.6) {
    const near = nearestColour(offline.alias.hex, palette);
    if (near.colour) {
      return {
        pinned: {
          colour: near.colour,
          matchType: "alias",
          aliasSource: offline.alias,
          distance: near.distance,
        },
        directMatches: [],
      };
    }
  }

  // Stage 5 — loose name-match tail.
  const loose = palette
    .map((c) => ({ ...c, score: nameScore(q, c.name) }))
    .filter((c) => c.score >= 0.35)
    .sort((a, b) => b.score - a.score)
    .slice(0, 6);
  return { pinned: null, directMatches: loose };
}
